import { forwardRef } from "react"
import type { ButtonHTMLAttributes, InputHTMLAttributes, SelectHTMLAttributes, TextareaHTMLAttributes } from "react"

/**
 * Gemeinsame UI-Primitiven nach UX_KONZEPT.md §6.6 (Design-Tokens).
 * Rezepte 1:1 aus dem Konzept übernommen — App-Akzent NIE auf Buttons,
 * nur neutrale Slate-Töne + Fokus-Ring `ring-slate-400/60`.
 */

const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"

function cx(...classes: Array<string | false | undefined>) {
  return classes.filter(Boolean).join(" ")
}

type ButtonVariant = "primary" | "secondary" | "destructive"

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
}

const BUTTON_BASE = "inline-flex h-9 items-center gap-2 rounded-md px-4 text-sm font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none"

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-slate-900 text-white hover:bg-slate-700",
  secondary: "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
  destructive: "bg-red-600 text-white hover:bg-red-700",
}

/** Fertige Klassenstrings für Nicht-`<button>`-Elemente (z. B. `<Link>`), die wie ein Button aussehen sollen. */
export const BUTTON_PRIMARY_CLASS = cx(BUTTON_BASE, BUTTON_VARIANTS.primary, FOCUS_RING)
export const BUTTON_SECONDARY_CLASS = cx(BUTTON_BASE, BUTTON_VARIANTS.secondary, FOCUS_RING)
export const BUTTON_DESTRUCTIVE_CLASS = cx(BUTTON_BASE, BUTTON_VARIANTS.destructive, FOCUS_RING)

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "primary", className, type = "button", ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cx(BUTTON_BASE, BUTTON_VARIANTS[variant], FOCUS_RING, className)}
      {...props}
    />
  )
})

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  invalid?: boolean
}

const FIELD_BASE =
  "h-9 w-full rounded-md border bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:border-slate-400 disabled:opacity-50 disabled:bg-slate-50"

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, className, ...props },
  ref
) {
  return (
    <input
      ref={ref}
      className={cx(FIELD_BASE, invalid ? "border-red-500" : "border-slate-300", className)}
      aria-invalid={invalid || undefined}
      {...props}
    />
  )
})

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { invalid, className, children, ...props },
  ref
) {
  return (
    <select
      ref={ref}
      className={cx(FIELD_BASE, invalid ? "border-red-500" : "border-slate-300", className)}
      aria-invalid={invalid || undefined}
      {...props}
    >
      {children}
    </select>
  )
})

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, ...props },
  ref
) {
  return (
    <textarea
      ref={ref}
      className={cx(
        "w-full rounded-md border bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-400/60 focus:border-slate-400 disabled:opacity-50 disabled:bg-slate-50",
        invalid ? "border-red-500" : "border-slate-300",
        className
      )}
      aria-invalid={invalid || undefined}
      {...props}
    />
  )
})

/** Fehlertext unter einem Formularfeld, Muster §6.6. */
export function FieldError({ id, children }: { id?: string; children: React.ReactNode }) {
  if (!children) return null
  return (
    <p id={id} className="mt-1 text-xs text-red-600">
      {children}
    </p>
  )
}

type BadgeTone = "green" | "orange" | "red" | "slate"

const BADGE_TONES: Record<BadgeTone, string> = {
  green: "bg-green-100 text-green-800",
  orange: "bg-orange-100 text-orange-800",
  red: "bg-red-100 text-red-800",
  slate: "bg-slate-100 text-slate-700",
}

export function Badge({
  tone = "slate",
  className,
  children,
}: {
  tone?: BadgeTone
  className?: string
  children: React.ReactNode
}) {
  return (
    <span
      className={cx(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
        BADGE_TONES[tone],
        className
      )}
    >
      {children}
    </span>
  )
}

/** Tabellen-Wrapper nach §6.6 (Karte + Rahmen + Schatten). */
export function TableWrapper({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cx("overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm", className)}>
      {children}
    </div>
  )
}

export const TH_CLASS = "px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-400"
export const TR_CLASS = "border-t border-slate-100 hover:bg-slate-50"
export const TD_CLASS = "px-4 py-2.5 text-sm text-slate-700"

export { FOCUS_RING }
